import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { User, Mail, Phone, Calendar, MapPin, Stethoscope, Hash, Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { formatDate, formatOrganizationTimezoneDisplay, formatUSPhoneNumber } from '@/utils/helpers';

const ProviderProfile = ({
  profileData,
  updatePassword,
  accessModule,
  isHideNpi,
  hideZipCode,
  editProfile,
}: {
  profileData: any;
  updatePassword?: () => void;
  /** e.g. `canAccessClinicians` — admin + managers with that module permission */
  accessModule?: string;
  isHideNpi?: boolean;
  hideZipCode?: boolean;
  editProfile: (data: any) => void;
}) => {
  const { user, canAccess } = useAuth();

  const canUpdatePassword =
    !!updatePassword &&
    (user?.role === "admin" ||
      (!!accessModule && canAccess(accessModule)));
  const providerData = profileData || {
    "_id": "691d86db128e8e714e9f4cf9",
    "firstName": "asdf sadf",
    "middleName": "Tatyana Wiley",
    "lastName": "Morris",
    "dateOfBirth": "1983-05-13",
    "email": "jafifit220@agenra.com",
    "phone": "+13318241677",
    "street": "Quia et delectus ve",
    "city": "Doloribus commodi re",
    "state": "Quis eligendi expedi",
    "zipcode": "73461",
    "gender": "male",
    "npiNumber": "485",
    "specializations": ["Et aut quos ex offic", "Another specialization"],
    "taxonomies": ["Sunt illo amet debi"],
    "status": "active"
  };

  const UserStatus : Record<string, string> = {
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    blocked: "Blocked",
  }

  const fullName = `${providerData.firstName 
  || ""} ${providerData.middleName || ""} ${providerData.lastName || ""}`;
  const timezoneDisplay = formatOrganizationTimezoneDisplay(providerData?.timezone);

  return (
    <div className="w-full mx-auto p-0 mb-4">
      <Card className="overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 p-6 border-b">
          <div className="flex items-start justify-between">
            <div className="flex gap-6">
              {/* Avatar with status */}
              <div className="relative">
                <div className="w-28 h-28 rounded-full bg-white shadow-lg flex items-center justify-center ring-4 ring-primary/20">
                  <User className="w-14 h-14 text-primary" />
                </div>
                <Badge className="absolute bottom-0 right-0 bg-green-500 hover:bg-green-600 shadow-md">
                  {providerData?.status ? UserStatus[providerData?.status] ?? "" : ""}
                </Badge>
              </div>

              {/* Name and Quick Info */}
              <div className="space-y-3 pt-2">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-1">{fullName}</h1>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{providerData.email}</span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {providerData?.specializations &&  providerData?.specializations?.map((spec:any, idx:any) => (
                    <Badge key={idx} variant="secondary" className="bg-primary text-primary-foreground hover:bg-primary/90">
                      <Stethoscope className="w-3 h-3 mr-1" />
                      {spec}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className='flex items-center gap-2'>

{canUpdatePassword && (
              <Button
                type="button"
                onClick={updatePassword}
                variant="outline"
                className="shadow-md"
              >
                <Pencil /> Update Password
              </Button>
            )}
            
            <Button onClick={()=> editProfile(profileData)}  className="shadow-md">
              <Pencil/> Edit Profile
            </Button>
              </div>
          </div>
        </div>

        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </h3>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Date of Birth</p>
                    <p className="font-medium">{formatDate(providerData.dateOfBirth)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Gender</p>
                    <p className="font-medium capitalize">{providerData.gender}</p>
                  </div>
                </div>
              {!isHideNpi &&   <div className="flex items-center gap-3">
                  <Hash className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">NPI Number</p>
                    <p className="font-medium">{providerData.npiNumber}</p>
                  </div>
                </div>}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contact Details
              </h3>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Mobile Number</p>
                    <p className="font-medium">{formatUSPhoneNumber(providerData.phone)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Email Address</p>
                    <p className="font-medium break-all">{providerData.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Address
                </h3>
                {/* <Button variant="outline" size="sm" className="h-8">
                  Update
                </Button> */}
              </div>
              <Separator />
              <div className="space-y-3">
                
                <div className="grid grid-cols-2 gap-3">
                     <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Street</p>
                    <p className="font-medium">{providerData.street}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">City</p>
                    <p className="font-medium">{providerData.city}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">State</p>
                    <p className="font-medium">{providerData.state}</p>
                  </div>
                  {!hideZipCode && (
                   <div>
                    <p className="text-xs text-muted-foreground">Zip Code</p>
                    <p className="font-medium">{providerData.zipcode}</p>
                  </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Timezone</p>
                    <p className="font-medium">{timezoneDisplay}</p>
                  </div>
                </div>
               
              </div>
            </div>
          </div>

          {/* Professional Details Section */}
          {/* <Separator className="my-6" /> */}
          {/* <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Stethoscope className="w-5 h-5" />
              Professional Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Specializations</p>
                <div className="flex flex-wrap gap-2">
                  {providerData.specializations.map((spec, idx) => (
                    <Badge key={idx} variant="outline" className="border-primary/30">
                      {idx + 1}. {spec}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Taxonomies</p>
                <div className="flex flex-wrap gap-2">
                  {providerData.taxonomies.map((tax, idx) => (
                    <Badge key={idx} variant="outline" className="border-primary/30">
                      {tax}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div> */}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderProfile;